import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  asyncGetItem,
  asyncGetList,
} from "@/setup/flexbiz-api-provider/coreFunction";
import { staleTime } from "@/types/projectConsts";
import productKeys from "../queryKeys/productKeys";

const COLLECTION_LOADING_NUMBER = Number(
  process.env.COLLECTION_LOADING_NUMBER || 16,
);

/* ─── fetch single product ─── */
export const fetchProduct = async ({
  ma_vt,
  signal,
}: {
  ma_vt: string;
  signal?: AbortSignal;
}) => {
  const result = await asyncGetItem<any[]>({
    collection_name: "dmvt",
    signal,
    options: {
      condition: {
        status: true,
        by_user: true,
        online: true,
        ma_vt,
        shared: true,
        gia_theo_kh: true,
      },
    },
  });
  return result?.[0] || null;
};

export const useFetchProduct = ({ ma_vt }: { ma_vt?: string }) =>
  useQuery({
    queryKey: productKeys.detail(ma_vt),
    queryFn: ({ signal }) => fetchProduct({ ma_vt: ma_vt!, signal }),
    staleTime: staleTime.LONG,
    enabled: !!ma_vt,
  });

/* ─── fetch product list ─── */
export const fetchProducts = async ({
  options = {} as any,
  signal,
  ma_nvt,
}: {
  options?: any;
  signal?: AbortSignal;
  ma_nvt?: string[];
}) => {
  const conditionManvt = ma_nvt
    ? { ma_nvt: { $in: [...ma_nvt] } }
    : null;

  const mergedCondition = {
    ...(options.condition || {}),
    ...(conditionManvt || {}),
    shared: true,
    gia_theo_kh: true,
    status: true,
  };

  return asyncGetList<any[]>({
    collection_name: "dmvt",
    signal,
    options: {
      ...options,
      condition: mergedCondition,
    },
  });
};

export const useFetchProducts = (options?: any) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: productKeys.list(options),
    queryFn: ({ signal }) => fetchProducts({ options, signal }),
    enabled: !!options,
    staleTime: staleTime.LONG,
    select: (products: any[]) => {
      products?.forEach((product: any) => {
        queryClient.setQueryData(
          productKeys.detail(product?.ma_vt),
          product,
        );
      });
      return products;
    },
  });
};

/* ─── fetch product collection (paginated + filterable) ─── */
export const useFetchProductCollection = (options: any, ma_nvt?: string[]) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: productKeys.list({
      page: options?.page || 1,
      sort: options?.sort,
      condition: options?.condition,
      ma_nvt: ma_nvt?.join("|"),
    }),
    queryFn: ({ signal }) =>
      fetchProducts({
        options: { ...options, limit: COLLECTION_LOADING_NUMBER },
        signal,
        ma_nvt,
      }),
    enabled: !!ma_nvt && ma_nvt.length > 0,
    staleTime: staleTime.LONG,
    placeholderData: (prev: any) => prev, // keepPreviousData equivalent in v5
    select: (products: any[]) => {
      products?.forEach((product: any) => {
        queryClient.setQueryData(
          productKeys.detail(product?.ma_vt),
          product,
        );
      });
      return products;
    },
  });
};

/* ─── fetch products count ─── */
export const fetchProductsCount = async ({
  options = {} as any,
  signal,
}: {
  options?: any;
  signal?: AbortSignal;
}) => {
  const data = await asyncGetList<any>({
    collection_name: "dmvt",
    signal,
    options: {
      count: true,
      condition: {
        ...(options?.condition || {}),
        status: true,
        shared: true,
      },
    },
  });
  return data?.rows_number || 0;
};

export const useFetchProductCount = ({ options }: { options?: any }) =>
  useQuery({
    queryKey: productKeys.count(options),
    queryFn: ({ signal }) => fetchProductsCount({ options, signal }),
    staleTime: staleTime.LONG,
    enabled: !!options,
  });

/* ─── related products ─── */
export const useFetchRelatedProducts = ({ ma_nvt }: { ma_nvt?: string }) => {
  return useQuery({
    queryKey: productKeys.relatedProducts(ma_nvt),
    queryFn: ({ signal }) =>
      fetchProducts({
        signal,
        options: {
          limit: 6,
          sort: { date_created: -1 },
          condition: { ma_nvt: { $in: [ma_nvt] } },
        },
      }),
    enabled: Boolean(ma_nvt),
    staleTime: staleTime.LONG,
  });
};
